import React from "react";
import { Box, Typography, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent } from "@mui/material";
import { Delete, Add } from "@mui/icons-material";
import PortfolioUpload from "../PortfolioUpload";
import ProjectCard from "./ProjectCard";
import EmptyPortfolioState from "./EmptyPortfolioState";
import { usePortfolioData } from "./usePortfolioData";

const Portfolio = ({ readOnly = false }) => {
  const {
    portfolios,
    loading,
    uploadModalOpen,
    editingProject,
    deleteDialogOpen,
    projectToDelete,
    deleting,
    getOrderedImages,
    handleUploadSuccess,
    handleCreateAlbum,
    handleEditProject,
    handleDeleteProject,
    handleCloseModal,
    handleCloseDeleteDialog,
    confirmDelete
  } = usePortfolioData('full');

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If we have portfolio data, show the actual portfolio
  if (portfolios.length > 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, margin: 0, fontSize: { xs: '18px', md: '24px' } }}
          >
            Portfolio
          </Typography>
          {!readOnly && (
            <Button
              variant="contained"
              onClick={handleCreateAlbum}
              sx={{
                backgroundColor: '#4D6BF6',
                textTransform: 'none',
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: 1,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#3B5BDB',
                }
              }}
            >
              Create Album
            </Button>
          )}
        </Box>
        
        {/* Portfolio Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 320px))',
            gap: { xs: 2, md: 3 },
            justifyContent: 'flex-start',
          }}
        >
          {/* Existing Portfolio Projects */}
          {portfolios.map((project) => {
            const orderedImages = getOrderedImages(project);
            return (
              <ProjectCard
                key={project.id}
                project={project}
                orderedImages={orderedImages}
                onEdit={readOnly ? undefined : handleEditProject}
                onDelete={readOnly ? undefined : handleDeleteProject}
                showFullDescription={true}
                readOnly={readOnly}
              />
            );
          })}

          {/* Create New Album Card - Last Item - Only show if not readOnly */}
          {!readOnly && (
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: '2px dashed #D1D5DB',
                backgroundColor: '#FAFAFA',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#4D6BF6',
                  backgroundColor: '#F8FAFF',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(77, 107, 246, 0.15)'
                }
              }}
              onClick={handleCreateAlbum}
            >
            <CardContent sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '200px',
              textAlign: 'center',
              p: 3,
              height: '100%'
            }}>
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                border: '2px solid #D1D5DB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                transition: 'all 0.2s ease'
              }}>
                <Add sx={{ fontSize: 30, color: '#6B7280' }} />
              </Box>
              <Typography variant="body1" sx={{ 
                color: '#374151', 
                fontWeight: 500,
                fontSize: '16px'
              }}>
                Create a new album
              </Typography>
            </CardContent>
          </Card>
          )}
        </Box>


        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Project</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{projectToDelete?.project_name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <PortfolioUpload
          key={editingProject?.id || 'new'}
          open={uploadModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleUploadSuccess}
          editingProject={editingProject}
        />
      </Box>
    );
  }

  // Show empty state
  return (
    <>
      <Box sx={{ p: 2 }}>
        <EmptyPortfolioState onCreateAlbum={readOnly ? undefined : handleCreateAlbum} readOnly={readOnly} />
      </Box>
      <PortfolioUpload
        key={editingProject?.id || 'new'}
        open={uploadModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleUploadSuccess}
        editingProject={editingProject}
      />
    </>
  );
};

export default Portfolio;